"use client";

import { setDPMenuCookie } from "@/app/actions";
import type { DayPart, DayPartMenuCookie } from "@/dto/dayPartMenu";
import { RestaurantCart } from "@/models/cart/restaurantCart";
import { ROUTE_MENU_ALL } from "@/utils/constants";
import {
  removeFromSessionStorage,
  saveToSessionStorage,
} from "@/utils/sessionStorageUtils";
import { SessionStorageKey } from "@/utils/storageKeys";
import { useCallback } from "react";

interface UseDayPartMenuHandlersParams {
  isDineIn: boolean;
  restaurantCartLength: number;
  selectedMenu: DayPart | null;
  searchParams: URLSearchParams | null;
  setIsClearCartOpen: (open: boolean) => void;
  setSelectedMenu?: (menu: DayPart | null) => void;
  tempMenu: { current: DayPart | null };
  isUserInitiatedDayPartSwitch: { current: boolean };
  restaurantMfeEnabled?: boolean;
  isDayPartsEnabled?: boolean;
}

export function useDayPartMenuHandlers({
  isDineIn,
  restaurantCartLength,
  selectedMenu,
  searchParams,
  setIsClearCartOpen,
  setSelectedMenu,
  tempMenu,
  isUserInitiatedDayPartSwitch,
  restaurantMfeEnabled = false,
  isDayPartsEnabled = true,
}: UseDayPartMenuHandlersParams) {
  const switchDayPartMenu = useCallback(() => {
    if (!isDayPartsEnabled || !tempMenu.current) return;

    setSelectedMenu?.(tempMenu.current);
    removeFromSessionStorage(SessionStorageKey.SCHEDULED_TIME);
    removeFromSessionStorage(SessionStorageKey.PICKUP_TIME);
    const payload: DayPartMenuCookie = {
      id: tempMenu.current.id,
      menuId: tempMenu.current.menuId,
      name: tempMenu.current.formattedName,
    };
    saveToSessionStorage(SessionStorageKey.DAYPART_MENU, payload);
    setDPMenuCookie(payload);
    const params = new URLSearchParams(searchParams || "");
    params.set("menu", tempMenu.current.formattedName);
    globalThis.location.assign(
      `${ROUTE_MENU_ALL(restaurantMfeEnabled)}?${params.toString()}`
    );
  }, [isDayPartsEnabled, restaurantMfeEnabled, searchParams, setSelectedMenu, tempMenu]);

  const onDayPartMenuChange = useCallback(
    (menu: DayPart | null) => {
      const shouldShowClearCartForMenuSwitch =
        restaurantCartLength > 0 && !isDineIn && selectedMenu?.id !== menu?.id;

      isUserInitiatedDayPartSwitch.current = true;
      tempMenu.current = menu;
      if (shouldShowClearCartForMenuSwitch) {
        setIsClearCartOpen(true);
        return;
      }

      switchDayPartMenu();
    },
    [
      isDineIn,
      restaurantCartLength,
      selectedMenu,
      setIsClearCartOpen,
      switchDayPartMenu,
      tempMenu,
      isUserInitiatedDayPartSwitch,
    ]
  );

  const onConfirmation = useCallback(() => {
    RestaurantCart.clear();
    setIsClearCartOpen(false);
    switchDayPartMenu();
  }, [setIsClearCartOpen, switchDayPartMenu]);

  return { switchDayPartMenu, onDayPartMenuChange, onConfirmation };
}
